<script lang="ts">
export default {
  name: 'ProcessBinderModal',
};
</script>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { FileText, Loader, AlertCircle } from '@lucide/vue';
import type { DocumentWithOtherData } from '@/types/v2/Document';
import type { AIPrompt } from '@/types/ai';
import { newConverseInput, InvokeModelRequest } from '@/types/ai';
import { aiService } from '@/services/AIService';

const emit = defineEmits<{
  processed: [];
}>();

const isVisible = ref(false);
const binderDoc = ref<DocumentWithOtherData | null>(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const error = ref<string>('');

const prompts = ref<AIPrompt[]>([]);

const selectionState = reactive({
  selectedPromptName: '',
  selectedVersion: '',
  availableVersions: [] as AIPrompt[],
});

function formatVersion(version: string): string {
  if (version.toLowerCase() === 'draft') {
    return 'DRAFT';
  }
  return `v${version}`;
}

function startsWith(str: unknown, prefix: string): boolean {
  return typeof str === 'string' && str.startsWith(prefix);
}

const groupedPrompts = computed(() => {
  const groups = new Map<string, AIPrompt[]>();
  const filteredPrompts = prompts.value.filter((p) => !startsWith(p.promptId, 'new-'));

  filteredPrompts.forEach((prompt) => {
    const promptId = String(prompt.promptId) || 'unknown';
    if (!groups.has(promptId)) {
      groups.set(promptId, []);
    }
    groups.get(promptId)!.push(prompt);
  });

  groups.forEach((versions) => {
    versions.sort((a, b) => {
      const aIsDraft = a.version.toLowerCase() === 'draft';
      const bIsDraft = b.version.toLowerCase() === 'draft';
      if (aIsDraft && !bIsDraft) return -1;
      if (!aIsDraft && bIsDraft) return 1;
      if (aIsDraft && bIsDraft) {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
      const aVersion = parseInt(a.version) || 0;
      const bVersion = parseInt(b.version) || 0;
      return bVersion - aVersion;
    });
  });

  const displayGroups = new Map<string, AIPrompt[]>();
  groups.forEach((versions) => {
    const draftVersion = versions.find((p) => p.version.toLowerCase() === 'draft');
    const displayName = draftVersion?.name || versions[0]?.name || 'Unnamed';
    displayGroups.set(displayName, versions);
  });

  return new Map([...displayGroups.entries()].sort(([a], [b]) => a.localeCompare(b)));
});

const promptOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select a prompt...' }];
  for (const [promptName] of groupedPrompts.value) {
    options.push({ value: promptName, label: promptName });
  }
  return options;
});

const versionOptions = computed(() => {
  return selectionState.availableVersions.map((version) => {
    let label = formatVersion(version.version);
    if ((version as { isActive?: boolean }).isActive) {
      label += ' (Active)';
    }
    return { value: version.version, label };
  });
});

const selectedPrompt = computed(() => {
  if (!selectionState.selectedPromptName || !selectionState.selectedVersion) return null;
  return selectionState.availableVersions.find((p) => p.version === selectionState.selectedVersion) ?? null;
});

const canProcess = computed(() => {
  return !!binderDoc.value && !!selectedPrompt.value && !isSubmitting.value;
});

const loadData = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    prompts.value = await aiService.getAllPrompts('internal');
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to load data';
    error.value = message;
    console.error('Failed to load prompts/models:', err);
  } finally {
    isLoading.value = false;
    await nextTick();
  }
};

const show = async (doc: DocumentWithOtherData) => {
  binderDoc.value = doc;
  isVisible.value = true;
  selectionState.selectedPromptName = '';
  selectionState.selectedVersion = '';
  selectionState.availableVersions = [];
  error.value = '';
  await loadData();
  prefillFromMapping(doc);
};

const prefillFromMapping = async (doc: DocumentWithOtherData) => {
  if (doc.mappedPromptId == null) return;

  for (const [name, versions] of groupedPrompts.value) {
    if (versions[0] && Number(versions[0].promptId) === doc.mappedPromptId) {
      selectionState.selectedPromptName = name;
      // Wait for the selectedPromptName watcher to populate availableVersions and
      // default selectedVersion before overriding to the mapped version.
      await nextTick();

      const mappedVersion = doc.mappedPromptVersion;
      if (mappedVersion && mappedVersion.toLowerCase() === 'latest') {
        const published = versions.find((v) => v.version.toLowerCase() !== 'draft');
        if (published) selectionState.selectedVersion = published.version;
      } else if (mappedVersion) {
        const match = versions.find((v) => v.version === mappedVersion);
        if (match) selectionState.selectedVersion = match.version;
      }
      return;
    }
  }
};

const hide = () => {
  isVisible.value = false;
  binderDoc.value = null;
  error.value = '';
  isSubmitting.value = false;
};

watch(
  () => selectionState.selectedPromptName,
  (newName) => {
    if (newName && groupedPrompts.value.has(newName)) {
      selectionState.availableVersions = groupedPrompts.value.get(newName)!;
      if (selectionState.availableVersions.length > 0) {
        selectionState.selectedVersion = selectionState.availableVersions[0].version;
      }
    } else {
      selectionState.availableVersions = [];
      selectionState.selectedVersion = '';
    }
  },
);

const handleProcess = async () => {
  if (!binderDoc.value || !selectedPrompt.value) return;

  const prompt = selectedPrompt.value;

  if (!prompt.modelArn) {
    error.value = 'This prompt does not have a model configured. Please update the prompt and try again.';
    return;
  }

  isSubmitting.value = true;
  error.value = '';

  try {
    const invokeRequest: InvokeModelRequest = {
      modelId: prompt.modelArn,
      converseInput: newConverseInput(prompt.userPrompt || '', prompt.systemPrompt),
      expectedOutputSchema: prompt.expectedOutputSchema || '',
      promptId: Number(prompt.promptId),
      promptVersion: prompt.version,
      documentId: binderDoc.value.documentId,
    };

    await aiService.invokeModelAsync(invokeRequest);
    emit('processed');
    hide();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to process binder';
    error.value = message;
    console.error('Process binder error:', err);
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({
  show,
  hide,
});
</script>

<template>
  <AppModal
    :show="isVisible"
    title="Process Binder"
    cancel-button-label="Cancel"
    action-button-label="Process"
    :handle-cancel="hide"
    :handle-action="handleProcess"
    :action-disabled="!canProcess"
    :loading="isSubmitting"
    :modal-class="'w-full max-w-lg'"
    :show-overflow="false">
    <template #content>
      <div class="space-y-4">
        <!-- Document (static) -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-slate-700">Document</label>
          <div class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <FileText class="h-4 w-4 text-slate-500" />
            <span class="text-sm text-slate-700">{{ binderDoc?.displayName ?? binderDoc?.fileName ?? 'No document' }}</span>
          </div>
        </div>

        <!-- Loading -->
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-6">
          <Loader class="mr-2 h-6 w-6 animate-spin text-blue-600" />
          <span class="text-slate-600">Loading prompts...</span>
        </div>

        <!-- Error -->
        <div
          v-else-if="error"
          class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <AlertCircle class="h-5 w-5 shrink-0 text-red-500" />
          <span class="text-sm text-red-700">{{ error }}</span>
        </div>

        <!-- Form -->
        <template v-else>
          <!-- Prompt -->
          <div class="space-y-2">
            <label class="text-sm font-medium text-slate-700">Prompt</label>
            <AppSelect
              v-model="selectionState.selectedPromptName"
              :options="promptOptions"
              placeholder="Select a prompt..."
              class="w-full" />
          </div>

          <!-- Version -->
          <div
            v-if="selectionState.selectedPromptName"
            class="space-y-2">
            <label class="text-sm font-medium text-slate-700">Version</label>
            <AppSelect
              v-model="selectionState.selectedVersion"
              :options="versionOptions"
              placeholder="Select version..."
              class="w-full" />
          </div>
        </template>
      </div>
    </template>
  </AppModal>
</template>
